import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  Select,
  TextArea,
  TextInput,
  formatDate,
} from "@odingard/design-system";
import type { VendorFinding, VendorFindingSeverity, Vendor } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { vendorApi } from "../../lib/api";
import { VendorDemoBanner } from "../../components/VendorMode";
import {
  FINDING_STATES,
  FINDING_STATE_LABEL,
  SEVERITIES,
  SEVERITY_LABEL,
  findingStateTone,
  severityTone,
} from "./labels";
import { message, usePeople } from "./helpers";

type Action = "progress" | "resolve" | "accept-risk" | "reassign";

export function VenFindings() {
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const people = usePeople(orgId);
  const [severity, setSeverity] = useState<VendorFindingSeverity | "">("");
  const [owner, setOwner] = useState("");
  const [raising, setRaising] = useState(false);
  const [acting, setActing] = useState<{ finding: VendorFinding; action: Action } | null>(null);

  const state = useAsync(
    async () =>
      vendorApi.findings(orgId, {
        ...(severity === "" ? {} : { severity }),
        ...(owner === "" ? {} : { ownerUserId: owner }),
      }),
    [orgId, severity, owner],
  );
  const findings = state.data?.data.findings ?? [];
  const demoMode = state.data?.demoMode ?? false;

  const vendors = useAsync(async () => (await vendorApi.vendors(orgId)).data.vendors, [orgId]);
  const vendorName = (id: string): string => (vendors.data ?? []).find((v) => v.id === id)?.legalName ?? id;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Findings</h1>
          <p>
            What a vendor has not satisfied, who is fixing it, and by when. Closing one is a governed move,
            and accepting the risk is an approval rather than a state change.
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "flex-end" }}>
          <div style={{ minWidth: "9rem" }}>
            <Field label="Severity">
              {(id) => (
                <Select
                  id={id}
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value as VendorFindingSeverity | "")}
                >
                  <option value="">Any</option>
                  {SEVERITIES.map((s) => (
                    <option key={s} value={s}>
                      {SEVERITY_LABEL[s]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          <div style={{ minWidth: "11rem" }}>
            <Field label="Owner">
              {(id) => (
                <Select id={id} value={owner} onChange={(e) => setOwner(e.target.value)}>
                  <option value="">Anyone</option>
                  {people.members.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.displayName || m.email}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          <Button onClick={() => setRaising(true)}>Raise a finding</Button>
        </div>
      </div>

      <VendorDemoBanner show={demoMode} />

      {state.loading && !state.data && <LoadingState label="Loading findings…" />}
      {state.error && <ErrorState description={state.error.message} onRetry={state.reload} />}
      {state.data && findings.length === 0 && (
        <EmptyState title="No findings" description="Nothing matches these filters." />
      )}

      {FINDING_STATES.map((column) => {
        const rows = findings.filter((f) => f.state === column);
        if (rows.length === 0) return null;
        return (
          <Card key={column}>
            <CardHeader
              title={
                <>
                  {FINDING_STATE_LABEL[column]} <Badge tone={findingStateTone(column)}>{rows.length}</Badge>
                </>
              }
            />
            <CardBody>
              {rows.map((f) => (
                <FindingRow
                  key={f.id}
                  finding={f}
                  vendorName={vendorName(f.vendorId)}
                  personName={people.name}
                  onAct={(action) => setActing({ finding: f, action })}
                />
              ))}
            </CardBody>
          </Card>
        );
      })}

      {/* Both dialogs are mounted only while open, so what was typed for one
          finding can never be carried into, or submitted against, another. */}
      {raising && (
        <RaiseDialog
          orgId={orgId}
          vendors={vendors.data ?? []}
          onClose={() => setRaising(false)}
          onRaised={() => {
            setRaising(false);
            state.reload();
          }}
        />
      )}
      {acting && (
        <ActionDialog
          key={`${acting.finding.id}:${acting.action}`}
          request={acting}
          orgId={orgId}
          onClose={() => setActing(null)}
          onDone={() => {
            setActing(null);
            state.reload();
          }}
        />
      )}
    </>
  );
}

function FindingRow({
  finding,
  vendorName,
  personName,
  onAct,
}: {
  finding: VendorFinding;
  vendorName: string;
  personName: (id: string | null | undefined) => string;
  onAct: (action: Action) => void;
}) {
  const closed = finding.state === "ACCEPTED_RISK" || finding.state === "RESOLVED";
  return (
    <section style={{ borderTop: "1px solid var(--ods-color-border)", paddingTop: "0.75rem", marginTop: "0.75rem" }}>
      <h3 style={{ marginBottom: "0.25rem" }}>
        {finding.title} <Badge tone={severityTone(finding.severity)}>{SEVERITY_LABEL[finding.severity]}</Badge>{" "}
        {finding.overdue && !closed ? <Badge tone="danger">Overdue</Badge> : null}
      </h3>
      <p style={{ color: "var(--ods-color-muted)" }}>
        <Link to={`/studio/vendor-ai/vendors/${finding.vendorId}`}>{vendorName}</Link>
        {finding.assessmentId ? (
          <>
            {" · "}
            <Link to={`/studio/vendor-ai/assessments/${finding.assessmentId}`}>from this assessment</Link>
          </>
        ) : null}
        {" · "}owned by {personName(finding.ownerUserId)} · due {formatDate(finding.dueOn)} · raised by{" "}
        {personName(finding.raisedBy)} on {formatDate(finding.raisedAt)}
      </p>
      {finding.description && <p>{finding.description}</p>}
      {closed ? (
        <p style={{ color: "var(--ods-color-muted)" }}>
          {FINDING_STATE_LABEL[finding.state]} by {personName(finding.closedBy)} on{" "}
          {formatDate(finding.closedAt)}
          {finding.closureNote ? ` · ${finding.closureNote}` : ""}
        </p>
      ) : (
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          {finding.state === "OPEN" && (
            <Button size="sm" variant="secondary" onClick={() => onAct("progress")}>
              Start work
            </Button>
          )}
          <Button size="sm" onClick={() => onAct("resolve")}>
            Resolve
          </Button>
          <Button size="sm" variant="secondary" onClick={() => onAct("accept-risk")}>
            Accept the risk
          </Button>
          <Button size="sm" variant="secondary" onClick={() => onAct("reassign")}>
            Reassign
          </Button>
        </div>
      )}
      {finding.history.length > 0 && (
        <details>
          <summary>History</summary>
          <ul>
            {finding.history.map((h) => (
              <li key={h.id}>
                {h.kind === "STATE"
                  ? `${h.fromState ? FINDING_STATE_LABEL[h.fromState] : "Raised"} → ${
                      h.toState ? FINDING_STATE_LABEL[h.toState] : ""
                    }`
                  : `Owner ${personName(h.fromOwner)} → ${personName(h.toOwner)}, due ${formatDate(
                      h.fromDueOn,
                    )} → ${formatDate(h.toDueOn)}`}{" "}
                by {personName(h.actorId)} on {formatDate(h.occurredAt)}
                {h.reason ? ` · ${h.reason}` : ""}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

/**
 * The refusals a finding can meet are different decisions, so they read
 * differently: the edge says the user does not hold the permission, and Vendor
 * AI says the person who raised a finding cannot be the one to accept its risk.
 */
function refusal(err: unknown): string {
  const text = message(err, "Could not record this.");
  if (text.toLowerCase().includes("raised")) {
    return `${text} Somebody else has to accept it. That separation is enforced by Vendor AI, not by this screen.`;
  }
  return text;
}

const ACTION_TITLE: Record<Action, string> = {
  progress: "Start work on this finding",
  resolve: "Resolve this finding",
  "accept-risk": "Accept the risk",
  reassign: "Reassign this finding",
};

function ActionDialog({
  request,
  orgId,
  onClose,
  onDone,
}: {
  request: { finding: VendorFinding; action: Action };
  orgId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const people = usePeople(orgId);
  const [note, setNote] = useState("");
  const [ownerUserId, setOwnerUserId] = useState("");
  const [dueOn, setDueOn] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setSaving(true);
    setError(null);
    try {
      const { finding, action } = request;
      if (action === "reassign") {
        await vendorApi.reassignFinding(orgId, finding.id, {
          ownerUserId: ownerUserId || finding.ownerUserId,
          dueOn: dueOn || finding.dueOn,
          reason: note.trim(),
        });
      } else if (action === "accept-risk") {
        await vendorApi.acceptFindingRisk(orgId, finding.id, note.trim());
      } else {
        await vendorApi.transitionFinding(
          orgId,
          finding.id,
          action === "resolve" ? "RESOLVED" : "IN_PROGRESS",
          note.trim(),
        );
      }
      setNote("");
      onDone();
    } catch (err) {
      setError(refusal(err));
    } finally {
      setSaving(false);
    }
  }

  const action = request.action;
  return (
    <Dialog
      open
      title={ACTION_TITLE[action]}
      description={
        action === "accept-risk"
          ? "Accepting the risk closes the finding without fixing it, needs vendors.approve, and is refused for the person who raised it."
          : request.finding.title
      }
      onClose={onClose}
      dismissible={!saving}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant={action === "accept-risk" ? "danger" : "primary"}
            onClick={run}
            disabled={saving || (action === "accept-risk" && note.trim().length < 5)}
          >
            {saving ? "Working…" : ACTION_TITLE[action]}
          </Button>
        </>
      }
    >
      {error && <p className="ods-failclosed">{error}</p>}
      {action === "reassign" && (
        <>
          <Field label="Owner">
            {(id) => (
              <Select id={id} value={ownerUserId} onChange={(e) => setOwnerUserId(e.target.value)}>
                <option value="">{people.name(request.finding.ownerUserId)} (unchanged)</option>
                {people.members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.displayName || m.email}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Due" help="Vendor AI owns this date.">
            {(id) => (
              <TextInput
                id={id}
                type="date"
                value={dueOn || (request.finding.dueOn ?? "")}
                onChange={(e) => setDueOn(e.target.value)}
              />
            )}
          </Field>
        </>
      )}
      <Field
        label={action === "accept-risk" ? "Why the organization is choosing to live with this" : "Note"}
        help="Kept with the finding."
      >
        {(id) => <TextArea id={id} rows={3} value={note} onChange={(e) => setNote(e.target.value)} />}
      </Field>
    </Dialog>
  );
}

function RaiseDialog({
  orgId,
  vendors,
  onClose,
  onRaised,
}: {
  orgId: string;
  vendors: Vendor[];
  onClose: () => void;
  onRaised: () => void;
}) {
  const people = usePeople(orgId);
  const [vendorId, setVendorId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState<VendorFindingSeverity>("MODERATE");
  const [ownerUserId, setOwnerUserId] = useState("");
  const [dueOn, setDueOn] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function raise() {
    setSaving(true);
    setError(null);
    try {
      await vendorApi.raiseFinding(orgId, {
        vendorId,
        title: title.trim(),
        description: description.trim(),
        severity,
        ownerUserId,
        dueOn,
      });
      onRaised();
    } catch (err) {
      setError(message(err, "Could not raise this finding."));
    } finally {
      setSaving(false);
    }
  }

  const ready = vendorId !== "" && title.trim() !== "" && ownerUserId !== "" && dueOn !== "";
  return (
    <Dialog
      open
      title="Raise a finding"
      description="A finding needs an owner and a date from the start. Nobody fixes a finding that belongs to everybody."
      onClose={onClose}
      dismissible={!saving}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={raise} disabled={saving || !ready}>
            {saving ? "Raising…" : "Raise"}
          </Button>
        </>
      }
    >
      {error && <p className="ods-failclosed">{error}</p>}
      <Field label="Vendor">
        {(id) => (
          <Select id={id} value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
            <option value="">Choose a vendor</option>
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>
                {v.legalName}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="Title">
        {(id) => <TextInput id={id} value={title} onChange={(e) => setTitle(e.target.value)} />}
      </Field>
      <Field label="What is wrong">
        {(id) => (
          <TextArea id={id} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        )}
      </Field>
      <Field label="Severity">
        {(id) => (
          <Select
            id={id}
            value={severity}
            onChange={(e) => setSeverity(e.target.value as VendorFindingSeverity)}
          >
            {SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {SEVERITY_LABEL[s]}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="Owner">
        {(id) => (
          <Select id={id} value={ownerUserId} onChange={(e) => setOwnerUserId(e.target.value)}>
            <option value="">Choose an owner</option>
            {people.members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.displayName || m.email}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="Due">
        {(id) => <TextInput id={id} type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />}
      </Field>
    </Dialog>
  );
}
